import { useRequiredContext } from "@redotech/react-util/context";
import { type LoadState, useLoad } from "@redotech/react-util/load";
import { AuthContext } from "@redotech/redo-merchant-app-common/auth";
import { REDO_MERCHANT_SERVER_URL } from "@redotech/redo-merchant-app-common/config";
import {
  createRedoOnboardingV2RpcClient,
  type RedoOnboardingV2RpcClient,
} from "@redotech/redo-onboarding-api/client";
import { createContext, memo, type ReactNode, useMemo } from "react";

export type ReturnsOnboardingResumeState = Awaited<
  ReturnType<RedoOnboardingV2RpcClient["getResumeState"]>
>;

export const ReturnsOnboardingResumeStateContext = createContext<
  LoadState<ReturnsOnboardingResumeState> | undefined
>(undefined);

ReturnsOnboardingResumeStateContext.displayName =
  "ReturnsOnboardingResumeStateContext";

export interface ReturnsOnboardingResumeStateProviderProps {
  children: ReactNode;
}

export const ReturnsOnboardingResumeStateProvider = memo(
  function ReturnsOnboardingResumeStateProvider({
    children,
  }: ReturnsOnboardingResumeStateProviderProps) {
    const auth = useRequiredContext(AuthContext);
    const client = useMemo(
      () =>
        createRedoOnboardingV2RpcClient({
          baseURL: new URL(`${REDO_MERCHANT_SERVER_URL}/onboarding-v2-rpc`),
          headers: { Authorization: auth.token },
        }),
      [auth.token],
    );
    const state = useLoad(
      (signal) => client.getResumeState({ productId: "returns" }, { signal }),
      [client],
    );

    return (
      <ReturnsOnboardingResumeStateContext.Provider value={state}>
        {children}
      </ReturnsOnboardingResumeStateContext.Provider>
    );
  },
);

export function useReturnsOnboardingResumeState(): LoadState<ReturnsOnboardingResumeState> {
  return useRequiredContext(ReturnsOnboardingResumeStateContext);
}
